import { defineStore, storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import {
	generateAnswers,
	generateSingleAnswer,
	rewriteAnswer,
	approveAnswers,
	updateAnswer as updateAnswerApi,
} from '../api/auto-apply-answers';
import { enqueueSubmissions } from '../api/auto-apply-submissions';
import autoApplyApi from '../api/auto-apply';

import { setAuthState } from '../composables/auth.js';
import { isAutoApplyQueueEnabled } from '../composables/autoapply-queue';
import { pollAnswerGeneration } from '../composables/usePollAnswerGeneration';

import {
	LOCATION_FIELD_SLUGS, isAnswerEmpty, looksLikePhone, formatAnswers,
} from '../utils/auto-apply-fields.js';
import { isValidEmailAddress } from '../utils/validators.js';
import { useNotifications } from './notifications.store';
import { useJobBoardStore } from './job-board.store.js';
import { useJobQualitySurveyStore } from './job-quality-survey.store.js';
import { useSelectResumeStore } from './select-resume.store.js';
import { usePrimaryResumeStore } from './primary-resume.store';
import { useFeatureUsageStore } from './feature-usage.store';
import { useSubscriptionPlanStore } from './subscription.store';
import { useOnboardingRetentionStore } from './onboarding-retention.store';
import { useUserEvents, USER_EVENTS } from './user-events.store';
// autoapply-queue.store.js imports useAutoApplyJobsStore back, but each store calls the other only inside its
// actions (never at setup or module-eval time), so the cycle is inert at runtime.
// eslint-disable-next-line import/no-cycle
import { useAutoApplyQueueStore } from './autoapply-queue.store.js';

const hasExistingDocumentsForResume = (job, resume) => !!(job?.resume && job?.coverLetter)
	&& job?.tailoredFromResumeUuid === resume?.uuid;

export const useAutoApplyJobsStore = defineStore('auto-apply', () => {
	const notificationsStore = useNotifications();
	const jobBoardStore = useJobBoardStore();
	const selectResumeStore = useSelectResumeStore();
	const featureUsageStore = useFeatureUsageStore();
	const subscriptionStore = useSubscriptionPlanStore();
	const primaryResumeStore = usePrimaryResumeStore();

	const { getUser, userHaveAccessTo } = setAuthState();
	const { selectedJob } = storeToRefs(jobBoardStore);
	const { primaryResume } = storeToRefs(primaryResumeStore);
	const {
		showSideTab,
		showUnableToTailorModal,
		isResumeModalVisible,
		isUploadCustomModalVisible,
	} = storeToRefs(selectResumeStore);

	let statusListenerChannel = null;
	let isStatusListenerSetup = false;
	const pendingUpdates = new Map();
	// Bumped whenever a flow opens or is reset, so a flow cancelled or replaced mid-await
	// stops instead of spending a credit or tearing down its successor (EN-894).
	let activeFlowId = 0;

	const jobFunctionsLoaded = ref(false);
	const jobFunctions = ref(['Other']);
	const dashboardOnboarding = ref(false);

	const user = ref(getUser());
	const autopilot = ref(user.value?.user_information?.autopilot || false);
	const isAutoApplyPreviewVisible = ref(false);
	const isAutoApplySettingsVisible = ref(false);
	const isSelectingResume = ref(false);
	const selectedAutoApplyResume = ref(null);
	const generatedAnswers = ref(null);
	const currentAutoApplyJobId = ref(null);
	const tailoringDocuments = ref(false);
	const formParsingError = ref(null);
	const validationErrors = ref({});
	const isAutoApplyInProgress = ref(false);
	// EN-1066: the current flow submits without the review form (Complete Auto Apply).
	const isCompletingAutoApply = ref(false);
	const isGenerating = ref(false);
	const isPreviewHiddenForDocumentEditing = ref(false);
	// The { resume, job } the "Unable to tailor" modal can still apply to untailored (EN-894).
	const untailoredApply = ref(null);
	// The resume that couldn't be tailored to a job, as { resumeUuid, jobId }. Unlike untailoredApply
	// it outlives the modal, so "Try a different resume" doesn't offer it again for that job (EN-1149).
	const untailorableResume = ref(null);
	// EN-1314: set when a paid user reaches the AutoApply technical limit — they get the technical-limit modal
	// rather than the upgrade paywall a free user sees. Shape: { job }.
	const autoApplyLimitReached = ref(null);

	const isAutoApplyEligible = computed(() => isJobEligibleForAutoApply(selectedJob.value));

	const untailorableResumeUuid = computed(() => {
		if (!untailorableResume.value || untailorableResume.value.jobId !== selectedJob.value?.provider_job_id) {
			return null;
		}

		return untailorableResume.value.resumeUuid;
	});

	watch(selectedJob, () => {
		if (isAutoApplyPreviewVisible.value && !isAutoApplyInProgress.value) {
			resetAutoApplyFlow();
		}
	});

	// EN-809: editing a Tailored Resume/Cover Letter hides the AutoApply preview to make room
	// for the document editor (see hidePreviewToEditDocument). Once the editor's side tab
	// closes, bring the preview straight back — the user shouldn't have to click AutoApply
	// again to see their form.
	watch(showSideTab, (isOpen) => {
		if (!isOpen && isPreviewHiddenForDocumentEditing.value) {
			isPreviewHiddenForDocumentEditing.value = false;
			isAutoApplyPreviewVisible.value = true;
		}
	});

	watch(showUnableToTailorModal, (isOpen) => {
		if (!isOpen) {
			untailoredApply.value = null;
		}
	});

	// A resume picker opened for AutoApply and then dismissed must not route the next,
	// unrelated pick (e.g. job details' Tailor Resume) into AutoApply, which spends a credit.
	// The upload modal counts as the same picker: it replaces it rather than dismissing it.
	watch(
		() => isResumeModalVisible.value || isUploadCustomModalVisible.value,
		(isPickerOpen) => {
			if (!isPickerOpen) {
				isSelectingResume.value = false;
			}
		},
	);

	const hidePreviewToEditDocument = () => {
		isPreviewHiddenForDocumentEditing.value = true;
		isAutoApplyPreviewVisible.value = false;
	};

	const formattedAnswers = computed(() => formatAnswers(generatedAnswers.value ?? []));

	const isJobEligibleForAutoApply = (job) => {
		return job?.ats_platform?.has_form_parser === true;
	};

	const isJobAutoApplySupported = (job) => {
		return job?.ats_platform?.is_auto_apply_supported === true;
	};

	// EN-1116: a 202 means the answers are being generated on the queue; wait for them. A poll already
	// in flight when the flow was replaced still settles, and its result belongs to the old flow.
	const awaitQueuedAnswers = async (result, flowId) => {
		const settled = result.status === 202
			? await pollAnswerGeneration(result.data.data.uuid, { isCancelled: () => !isCurrentFlow(flowId) })
			: result;

		return isCurrentFlow(flowId) ? settled : null;
	};

	// A replaced flow must not clear the generating state, or set the error, of the flow that replaced it.
	const finishGenerating = (flowId, error = null) => {
		if (isCurrentFlow(flowId)) {
			isGenerating.value = false;
			formParsingError.value = error ?? formParsingError.value;
		}
	};

	/**
	 * Out of AutoApply credits: a free user gets the upgrade paywall. A paid user has hit the AutoApply technical
	 * limit (EN-1314), so they get its modal, and each show is logged.
	 * Shared by answer generation and the AutoApply Queue's add (EN-1236).
	 */
	const showOutOfCreditsPaywall = (job, { has_premium_access: hasPremiumAccess }) => {
		if (hasPremiumAccess) {
			autoApplyLimitReached.value = { job };
			// Best effort: trackEvent rethrows an API failure, and a lost log must not break the modal.
			useUserEvents().trackEvent(USER_EVENTS.AUTOAPPLY_TECHNICAL_LIMIT_SHOWN, {
				job_board_job_id: job.provider_job_id,
			}).catch(() => {});
		} else {
			featureUsageStore.setShowLimitExceed('auto_apply_v2');
		}
	};

	const generateAiAnswers = async (job, resume) => {
		if (isGenerating.value) { return; }
		isGenerating.value = true;
		const flowId = activeFlowId;
		let result;
		try {
			result = await awaitQueuedAnswers(await generateAnswers(job.provider_job_id, resume.uuid), flowId);
		} catch (e) {
			finishGenerating(flowId, { type: 'failed', message: 'Unable to reach the server. Please try again.' });
			throw e;
		} finally {
			finishGenerating(flowId);
			featureUsageStore.refresh();
		}

		if (!result) {
			return null;
		}

		// Only the token-exhausted 429 carries has_premium_access; a throttle:api 429 does not and
		// falls through to the generic error below rather than showing a paywall to a paying user.
		if (result.status === 429 && typeof result.data?.has_premium_access === 'boolean') {
			showOutOfCreditsPaywall(job, result.data);
			resetAutoApplyFlow();
			throw new Error('No tokens available.');
		}

		if (result.status >= 400) {
			const type = result.status === 410 ? 'expired' : 'failed';
			const defaultMessage = type === 'expired'
				? 'This job posting has expired.'
				: 'Unable to retrieve the application form.';
			formParsingError.value = {
				type,
				message: result.data?.message || defaultMessage,
			};
			throw new Error(formParsingError.value.message);
		}

		generatedAnswers.value = result.data.data;

		if (!generatedAnswers.value?.length) {
			formParsingError.value = {
				type: 'failed',
				message: 'Unable to retrieve the application form.',
			};
			throw new Error(formParsingError.value.message);
		}

		return generatedAnswers.value;
	};

	const generateSingleFieldAnswer = async (answerUuid) => {
		const result = await generateSingleAnswer(answerUuid);

		if (result.status >= 400) {
			throw new Error(result.data?.message || 'Failed to generate answer');
		}

		return result.data.data;
	};

	const regenerateAnswer = async (answerUuid, instruction) => {
		const result = await rewriteAnswer(answerUuid, instruction);

		if (result.status >= 400) {
			throw new Error(result.data?.message || 'Failed to rewrite answer');
		}

		return result.data.data;
	};

	// EN-1236: a queue user's click adds the job to the AutoApply Queue instead of opening the preview.
	const startQueueFlow = (job) => {
		const queueStore = useAutoApplyQueueStore();

		if (isSelectingResume.value || queueStore.addingJobId !== null || queueStore.applicationForJob(job) !== null) {
			return;
		}

		if (primaryResume.value) {
			queueStore.addToQueue(job, primaryResume.value);
		} else {
			isSelectingResume.value = true;
			selectResumeStore.showResumeModal();
		}
	};

	/**
	 * Start AutoApply on a job: the Recommended selection unless the caller names another, such as a job on the Saved
	 * tab (EN-1303).
	 */
	const startAutoApplyFlow = (job = selectedJob.value) => {
		// EN-1303: AutoApply is never started on an expired job. Saved loads once per session, so its copy of the job
		// can be stale, and a hand-off to Recommended would start the legacy flow, whose answer generation charges.
		if (job?.is_expired) {
			return;
		}

		if (isAutoApplyQueueEnabled()) {
			startQueueFlow(job);
			return;
		}

		// isSelectingResume: the resume picker is already open for this click; repeat clicks would clear its
		// selection and refetch resumes (EN-897). The picker-closed watcher above clears it.
		if (isAutoApplyInProgress.value || isAutoApplyPreviewVisible.value || isSelectingResume.value) {
			return;
		}

		if (primaryResume.value) {
			onResumeSelected(primaryResume.value, job);
		} else {
			isSelectingResume.value = true;
			selectResumeStore.showResumeModal();
		}
	};

	const onResumeSelected = async (resume, job) => {
		if (isAutoApplyQueueEnabled()) {
			isSelectingResume.value = false;
			return useAutoApplyQueueStore().addToQueue(job, resume);
		}

		// Guard against a second concurrent generate/tailor flow (e.g. "Change Resume" clicked
		// while the current one is still running), which would race the in-progress flow.
		if (isAutoApplyInProgress.value) {
			isSelectingResume.value = false;
			return;
		}

		// An AutoApply click moves the user out of the "engaged but didn't act" segment:
		// reset the job-quality-survey counters and close the prompt if it is open.
		useJobQualitySurveyStore().resetCounters();

		const isSameResume = selectedAutoApplyResume.value?.uuid && resume?.uuid === selectedAutoApplyResume.value.uuid;
		const isSameJob = currentAutoApplyJobId.value === job?.provider_job_id;

		if (isSameResume && isSameJob) {
			// Nothing to regenerate, but the preview may have been hidden to make room for the
			// Tailored Resume/Cover Letter editor (auto-apply-preview.vue's @editing handler calls
			// hidePreviewToEditDocument()), so re-show it rather than silently no-op'ing the AutoApply click.
			isSelectingResume.value = false;
			isAutoApplyPreviewVisible.value = true;
			return;
		}

		isSelectingResume.value = false;
		const completing = shouldCompleteAutoApply(job);
		const flowId = openPreview(resume, job, { showPreview: !completing });

		// EN-894: generating answers is what spends the AutoApply credit, so tailor first and
		// stop before the charge when the resume can't be tailored to this job. Out of credits
		// (never a subscriber), generating can't charge (it 429s, or returns this job's existing
		// answers), so it goes first to surface the paywall without paying for a tailor that
		// couldn't be used.
		if (hasAutoApplyCredits()) {
			await tailorThenGenerate(resume, job, flowId);
		} else {
			await generateThenTailor(resume, job, flowId);
		}

		if (completing) {
			await completeAutoApply(flowId, job);
		}

		finishFlow(flowId);
	};

	/**
	 * EN-1066 "Complete Auto Apply": the seeker opted in to skip the review form. Only jobs we
	 * submit ourselves qualify; external applies still need the seeker on the employer's site.
	 */
	const shouldCompleteAutoApply = (job) => userHaveAccessTo('complete-auto-apply')
		&& jobBoardStore.completeAutoApply
		&& isJobAutoApplySupported(job);

	/**
	 * Submit the generated pre-fill straight away when it passes the same validation the review
	 * form runs; otherwise fall back to the review form for this one application (missing
	 * required answers, parse failure, submit error) — the setting itself stays on.
	 */
	const completeAutoApply = async (flowId, job) => {
		isCompletingAutoApply.value = false;

		// The flow was superseded or reset (e.g. the "Unable to tailor" modal stopped it).
		if (!isCurrentFlow(flowId) || !isAutoApplyInProgress.value) {
			return;
		}

		// Validate the same shape the review form does (`answer`, not the API's `answer_text`).
		if (formParsingError.value || !formattedAnswers.value.length || !validateAnswers(formattedAnswers.value)) {
			isAutoApplyPreviewVisible.value = true;
			return;
		}

		try {
			await submitAnswers();
		} catch {
			isAutoApplyPreviewVisible.value = true;
			notificationsStore.notify('We couldn\'t submit this one automatically. Please review and submit.', 'error');
			return;
		}

		notificationsStore.notify(
			'Application queued for submission! Track its status in JobTrackerAI',
			'success',
			5000,
			'/job-tracker',
			'View in JobTrackerAI',
		);
		resetAutoApplyFlow();
		await jobBoardStore.changeJobApplyStatus(job, true);
		// As the review form's submit does (EN-639): a first application closes the first-run
		// exit-intent window (EN-1185) and may surface the paywall.
		await useOnboardingRetentionStore().recordFirstApplicationSubmitted();
	};

	const openPreview = (resume, job, { showPreview = true } = {}) => {
		currentAutoApplyJobId.value = job?.provider_job_id;
		selectedAutoApplyResume.value = resume;
		generatedAnswers.value = null;
		formParsingError.value = null;

		isAutoApplyInProgress.value = true;
		isAutoApplyPreviewVisible.value = showPreview;
		isCompletingAutoApply.value = !showPreview;

		activeFlowId += 1;

		return activeFlowId;
	};

	const isCurrentFlow = (flowId) => flowId === activeFlowId;

	const finishFlow = (flowId) => {
		if (isCurrentFlow(flowId)) {
			isAutoApplyInProgress.value = false;
		}
	};

	// A subscriber has no balance (their usage can be 0) and is never out of credits, so they count
	// as having them. isSubscribe is read off the store here, not via storeToRefs(), which crashes
	// on that store's plain stripeKey when VITE_STRIPE_KEY is unset (CI).
	const hasAutoApplyCredits = () => {
		const autoApplyUsage = featureUsageStore.userFeatureUsage?.find((feature) => feature.name === 'auto_apply_v2');

		return subscriptionStore.isSubscribe || autoApplyUsage?.usage > 0;
	};

	const generateAnswersForPreview = async (resume, job) => {
		try {
			await generateAiAnswers(job, resume);

			return true;
		} catch {
			return false;
		}
	};

	const tailorDocuments = async (resume, job) => {
		if (hasExistingDocumentsForResume(job, resume)) {
			return true;
		}

		tailoringDocuments.value = true;
		try {
			return await jobBoardStore.tailorResume(resume, job, 'auto_apply');
		} finally {
			tailoringDocuments.value = false;
		}
	};

	const tailorThenGenerate = async (resume, job, flowId) => {
		const isTailored = await tailorDocuments(resume, job);

		if (!isCurrentFlow(flowId)) {
			return;
		}

		// Only a "no overlap" result opens the modal and stops the flow. A failed request has
		// already toasted; the seeker can still apply with the untailored resume, as before.
		if (!isTailored && showUnableToTailorModal.value) {
			resetAutoApplyFlow();
			untailoredApply.value = { resume, job };
			untailorableResume.value = { resumeUuid: resume.uuid, jobId: job.provider_job_id };
			return;
		}

		await generateAnswersForPreview(resume, job);
	};

	const generateThenTailor = async (resume, job, flowId) => {
		if (await generateAnswersForPreview(resume, job) && isCurrentFlow(flowId)) {
			await tailorDocuments(resume, job);
		}
	};

	// The seeker chose to apply without a tailored resume: this generation spends the credit.
	const applyWithUntailoredResume = async () => {
		const { resume, job } = untailoredApply.value;
		selectResumeStore.hideUnableToTailorModal();

		const completing = shouldCompleteAutoApply(job);
		const flowId = openPreview(resume, job, { showPreview: !completing });
		await generateAnswersForPreview(resume, job);

		if (completing) {
			await completeAutoApply(flowId, job);
		}

		finishFlow(flowId);
	};

	const validateAnswers = (answers) => {
		const errors = {};
		answers.forEach((item) => {
			const fieldName = item.field?.field_name;
			if (!fieldName) {
				return;
			}

			const isEmpty = isAnswerEmpty(item);

			if (item.field.is_required && isEmpty) {
				const isLocationField = LOCATION_FIELD_SLUGS.includes(item.catalog_slug);
				errors[fieldName] = [
					isLocationField
						? 'Please update your profile with your location to fill this field.'
						: 'This field is required',
				];
				return;
			}

			if (isEmpty) {
				return;
			}

			if (item.catalog_slug === 'phone' && !looksLikePhone(item.answer)) {
				errors[fieldName] = ['Please enter a valid phone number.'];
			} else if (item.catalog_slug === 'email' && !isValidEmailAddress(item.answer)) {
				errors[fieldName] = ['Please enter a valid email address.'];
			}
		});
		validationErrors.value = errors;
		return Object.keys(errors).length === 0;
	};

	const clearFieldError = (fieldName) => {
		if (fieldName in validationErrors.value) {
			delete validationErrors.value[fieldName];
		}
	};

	// Replaces every field error at once, `{ field_name: [message] }`; with none given, clears them.
	const setValidationErrors = (errors = {}) => {
		validationErrors.value = errors;
	};

	const persistAnswer = (uuid, data) => {
		const existing = pendingUpdates.get(uuid);
		if (existing?.timer) {
			clearTimeout(existing.timer);
		}

		const timer = setTimeout(async () => {
			const entry = pendingUpdates.get(uuid);
			if (!entry) { return; }
			try {
				await updateAnswerApi(uuid, entry.data);
				pendingUpdates.delete(uuid);
			} catch {
				// Leave in pendingUpdates so flushPendingUpdates can retry
			}
		}, 500);

		pendingUpdates.set(uuid, { timer, data });
	};

	const flushPendingUpdates = async () => {
		const entries = [...pendingUpdates.entries()];
		if (entries.length === 0) { return; }

		for (const [, { timer }] of entries) {
			clearTimeout(timer);
		}

		const promises = entries.map(([uuid, { data }]) => updateAnswerApi(uuid, data));
		const results = await Promise.allSettled(promises);

		const failed = [];
		results.forEach((result, i) => {
			if (result.status === 'fulfilled') {
				pendingUpdates.delete(entries[i][0]);
			} else {
				failed.push(result);
			}
		});

		if (failed.length > 0) {
			throw new Error('Failed to save some answer edits. Please try again.');
		}
	};

	const clearPendingUpdates = () => {
		for (const { timer } of pendingUpdates.values()) {
			clearTimeout(timer);
		}
		pendingUpdates.clear();
	};

	const resetAutoApplyFlow = () => {
		activeFlowId += 1;
		clearPendingUpdates();
		isAutoApplyPreviewVisible.value = false;
		isAutoApplyInProgress.value = false;
		isCompletingAutoApply.value = false;
		isGenerating.value = false;
		isSelectingResume.value = false;
		selectedAutoApplyResume.value = null;
		generatedAnswers.value = null;
		currentAutoApplyJobId.value = null;
		formParsingError.value = null;
		tailoringDocuments.value = false;
		validationErrors.value = {};
	};

	const closeAutoApplyLimitReached = () => {
		autoApplyLimitReached.value = null;
	};

	const isExternalApply = computed(() => !selectedJob.value?.is_auto_apply_supported);

	const throwOnApproveError = (result) => {
		if (result.status === 422 && result.data?.errors) {
			validationErrors.value = result.data.errors;
			throw new Error(result.data.message || 'Please fix the highlighted fields and try again.');
		}
		if (result.status >= 400) {
			throw new Error(result.data?.message || 'Failed to approve answers');
		}
	};

	const submitAnswers = async (saveToMemoryIds = []) => {
		const answerIds = generatedAnswers.value?.map((a) => a.uuid) || [];
		if (answerIds.length === 0) {
			return;
		}

		// Flush any debounced edits so backend has current values
		await flushPendingUpdates();

		// Capture before any async operations to prevent stale reactive refs
		const jobBoardJobId = selectedJob.value?.provider_job_id;
		const isExternal = isExternalApply.value;
		// The search that surfaced this job, stamped on it when the results were mapped, for
		// the EN-838 satisfaction snapshot. Null when the seeker reached the job any other
		// way — a shared link, or the Saved tab.
		const criteriaQueryId = selectedJob.value?.criteriaQueryId ?? null;

		const result = await approveAnswers(answerIds, saveToMemoryIds);
		throwOnApproveError(result);

		if (!isExternal && jobBoardJobId) {
			const enqueueResult = await enqueueSubmissions([jobBoardJobId], 'autopilot', 0, criteriaQueryId);

			if (enqueueResult.status >= 400) {
				throw new Error(enqueueResult.data?.message || 'Failed to enqueue submission');
			}
		}

		await featureUsageStore.refresh();

		return result.data;
	};

	// Named so teardown can unbind this listener alone: other stores listen to the same event on the channel.
	const onStatusEvent = (event) => {
		if (event.status === 'submitted') {
			// EN-1245: the queue announces its own sends ("Application sent"), on every JobBoardAI tab.
			if (isAutoApplyQueueEnabled()) {
				return;
			}

			notificationsStore.notify(
				'Application submitted successfully! View it in JobTrackerAI',
				'success',
				5000,
				'/job-tracker',
				'View in JobTrackerAI',
			);
		} else if (event.status === 'failed') {
			// EN-1236: for a queue user a failure is a queue preparation ending in Couldn't apply, which every
			// Tier 2 job does (D1). The queue shows that outcome, so it gets no toast while they browse.
			if (!isAutoApplyQueueEnabled()) {
				notificationsStore.notify(
					`Application submission failed: ${event.failure_reason || 'Unknown error'}`,
					'error',
					5000,
				);
			}
			// The server refunds the credit on failure; keep the cached usage in step so the
			// next AutoApply tailors before it generates (see hasAutoApplyCredits).
			featureUsageStore.refresh();
		}
	};

	const setupStatusListener = () => {
		if (isStatusListenerSetup) { return; }

		const currentUser = getUser();
		if (!currentUser) { return; }

		statusListenerChannel = window.Echo.private(`auto-apply.${currentUser.id}`);
		statusListenerChannel.listen('AutoApplyQueueEvent', onStatusEvent);

		isStatusListenerSetup = true;
	};

	const teardownStatusListener = () => {
		if (!isStatusListenerSetup || !statusListenerChannel) { return; }

		statusListenerChannel.stopListening('AutoApplyQueueEvent', onStatusEvent);
		statusListenerChannel = null;
		isStatusListenerSetup = false;
	};

	const fetchJobFunctions = async () => {
		if (jobFunctionsLoaded.value) { return; }

		const res = await autoApplyApi.getJobFunctions();
		jobFunctions.value = ['Other', ...res.map((d) => d.functions)];
		jobFunctionsLoaded.value = true;
	};

	const setDashboardOnboarding = (value) => {
		dashboardOnboarding.value = value;
	};

	return {
		isAutoApplyEligible,
		isExternalApply,
		isAutoApplySettingsVisible,
		isSelectingResume,
		selectedAutoApplyResume,
		autopilot,
		isAutoApplyPreviewVisible,
		isAutoApplyInProgress,
		isCompletingAutoApply,
		currentAutoApplyJobId,
		generatedAnswers,
		tailoringDocuments,
		formParsingError,
		validationErrors,
		formattedAnswers,
		jobFunctions,
		jobFunctionsLoaded,
		dashboardOnboarding,
		untailoredApply,
		untailorableResumeUuid,
		autoApplyLimitReached,

		generateAiAnswers,
		showOutOfCreditsPaywall,
		generateSingleFieldAnswer,
		isJobEligibleForAutoApply,
		isJobAutoApplySupported,
		regenerateAnswer,
		persistAnswer,
		flushPendingUpdates,
		validateAnswers,
		clearFieldError,
		setValidationErrors,
		submitAnswers,
		startAutoApplyFlow,
		onResumeSelected,
		applyWithUntailoredResume,
		resetAutoApplyFlow,
		closeAutoApplyLimitReached,
		hidePreviewToEditDocument,
		setupStatusListener,
		teardownStatusListener,
		fetchJobFunctions,
		setDashboardOnboarding,
	};
});
